import type { ApiError as ApiErrorBody, ErrorCode } from '@dispositio/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

let csrfToken = '';
export const setCsrfToken = (t: string): void => {
  csrfToken = t;
};

/**
 * `raw`: eine Datei statt JSON (etwa eine ZIP-Datei zum Einlesen). Fehlt das CSRF-Token oder ist es veraltet (neu angemeldet in einem anderen
 * Fenster, Modul im Entwicklungsserver neu geladen), holt die Anfrage es einmal neu und versucht es noch einmal.
 */
async function request<T>(method: string, path: string, body?: unknown, raw?: Blob, retried = false): Promise<T> {
  const headers: Record<string, string> = {};
  if (raw) headers['content-type'] = raw.type || 'application/octet-stream';
  else if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers,
      ...(raw ? { body: raw } : body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError(0, 'network', 'Keine Verbindung zum Server.');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    parsed = text;
  }
  if (!res.ok) {
    const e = (parsed ?? {}) as Partial<ApiErrorBody>;
    if (e.code === 'csrf' && !retried) {
      const me = await request<{ csrfToken?: string } | undefined>('GET', '/auth/me').catch(() => undefined);
      if (me?.csrfToken) {
        csrfToken = me.csrfToken;
        return request<T>(method, path, body, raw, true);
      }
    }
    throw new ApiError(res.status, e.code ?? 'internal', e.message ?? `Fehler ${res.status}`, e.details);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T = void>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  /** Eine Datei senden (POST), mit ihrem Typ als content-type */
  upload: <T>(path: string, file: Blob) => request<T>('POST', path, undefined, file),
};

export const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : String(e));
